import { useState, useEffect, useRef } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "@/hooks/useAuth";
import ChangePasswordModal from "@/components/feature/ChangePasswordModal";
import { updateProfile, UpdateProfileData } from "@/utils/api";
import type { RegisterField } from "@/utils/adminApi";

// 标准字段（不可通过注册配置修改的基础信息）
const STANDARD_FIELDS = ["username", "nickname", "password", "confirm_password"];

// 获取当前站点ID
const getSiteId = () => localStorage.getItem("current_site_id") || "";

// 加载注册字段配置
async function loadRegisterFields(siteId: string): Promise<RegisterField[]> {
  const response = await fetch(`/api/register/config?site_id=${encodeURIComponent(siteId)}`);
  const data = await response.json();
  if (data.code !== 0 || !data.data?.fields) return [];
  // 只返回启用的自定义字段（排除标准字段和 phone，phone 单独处理）
  return data.data.fields.filter(
    (f: RegisterField) => f.is_enabled && !STANDARD_FIELDS.includes(f.field_name)
  );
}

// 上传图片到服务器
async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/api/upload", {
    method: "POST",
    body: formData,
  });

  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.message || "上传失败");
  }

  // 返回完整URL
  return data.url;
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // 动态注册字段配置
  const [registerFields, setRegisterFields] = useState<RegisterField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  
  // 头像相关
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [newAvatarFile, setNewAvatarFile] = useState<File | null>(null);

  // 图片字段相关
  const imageInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // 编辑表单数据
  const [editData, setEditData] = useState<Record<string, string>>({});

  // 加载注册字段配置
  useEffect(() => {
    const siteId = getSiteId();
    if (!siteId) {
      setFieldsLoading(false);
      return;
    }
    loadRegisterFields(siteId).then((fields) => {
      setRegisterFields(fields);
      setFieldsLoading(false);
    }).catch(() => {
      setFieldsLoading(false);
    });
  }, []);

  // 初始化编辑数据
  useEffect(() => {
    if (user && isEditing) {
      const data: Record<string, string> = {};
      // 手机号
      if (user.phone) data["phone"] = user.phone;
      // 自定义注册字段
      registerFields.forEach(field => {
        const value = user[field.field_name as keyof typeof user];
        if (value) data[field.field_name] = String(value);
      });
      setEditData(data);
      setAvatarPreview(user.avatar || "");
    }
  }, [user, isEditing, registerFields]);

  // 构建可显示/编辑的字段列表（动态）
  const getProfileFields = () => {
    if (!user) return [];
    const result: { key: string; label: string; type: string; value: string; options: string[] }[] = [];
    
    // 手机号字段（固定展示）
    result.push({
      key: "phone",
      label: "手机号码",
      type: "text",
      value: editData["phone"] || user.phone || "",
      options: [],
    });
    
    // 动态注册字段
    registerFields.forEach(field => {
      result.push({
        key: field.field_name,
        label: field.field_label,
        type: field.field_type,
        value: editData[field.field_name] || String(user[field.field_name as keyof typeof user] || ""),
        options: field.options || [],
      });
    });
    
    return result;
  };

  const profileFields = getProfileFields();

  // 处理头像上传
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 本地预览
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setNewAvatarFile(file);
  };

  // 处理图片字段上传
  const handleImageFieldUpload = async (fieldKey: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImage(file);
      setEditData({ ...editData, [fieldKey]: url });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '上传失败' });
    } finally {
      setIsUploading(false);
    }
  };

  // 处理编辑保存
  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    setMessage(null);
    
    try {
      const data: UpdateProfileData = {};
      
      // 处理头像上传
      if (newAvatarFile) {
        const avatarUrl = await uploadImage(newAvatarFile);
        data.avatar_url = avatarUrl;
      }
      
      // 处理手机号
      if (editData["phone"] !== undefined && editData["phone"] !== "") {
        data.phone = editData["phone"];
      }
      
      // 处理动态注册字段
      registerFields.forEach(field => {
        const value = editData[field.field_name];
        if (value !== undefined && value !== "") {
          (data as Record<string, string>)[field.field_name] = value;
        }
      });
      
      await updateProfile(data);
      
      setMessage({ type: 'success', text: '资料保存成功' });
      setIsEditing(false);
      setNewAvatarFile(null);
      
      // 刷新用户信息
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '保存失败' });
    } finally {
      setIsSaving(false);
    }
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    setEditData({});
    setAvatarPreview("");
    setNewAvatarFile(null);
  };

  return (
    <DashboardLayout>
      <div className="p-5 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-lg md:text-xl font-bold text-gray-800">
            个人资料
          </h1>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors text-sm"
              >
                <i className="ri-edit-line" />
                编辑资料
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors text-sm"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || isUploading}
                  className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors text-sm disabled:opacity-50"
                >
                  {isSaving || isUploading ? '保存中...' : '保存'}
                </button>
              </>
            )}
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors text-sm"
            >
              <i className="ri-key-line" />
              修改密码
            </button>
          </div>
        </div>

        <div className="max-w-2xl">
          {/* 用户信息卡片 */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* 头像和基本信息 */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-5">
                <div className="relative">
                  <img
                    src={isEditing ? (avatarPreview || user?.avatar || "https://via.placeholder.com/80") : (user?.avatar || "https://via.placeholder.com/80")}
                    alt={user?.username}
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-100"
                  />
                  {isEditing && (
                    <>
                      <button
                        onClick={() => avatarInputRef.current?.click()}
                        className="absolute -bottom-1 -right-1 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors shadow-md"
                      >
                        <i className="ri-camera-line text-sm" />
                      </button>
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleAvatarChange}
                      />
                    </>
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-1">
                    {user?.username}
                  </h2>
                  <p className="text-sm text-gray-500">
                    注册时间：{user?.created_at ? new Date(user.created_at).toLocaleDateString("zh-CN") : "-"}
                  </p>
                  {user?.isVip && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 text-yellow-600 text-xs rounded-full mt-1">
                      <i className="ri-vip-crown-line" />
                      VIP会员
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* 注册信息 */}
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                <i className="ri-file-user-line text-orange-500" />
                注册信息
              </h3>
              
              {fieldsLoading ? (
                <div className="py-8 text-center text-sm text-gray-400">
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  加载字段配置...
                </div>
              ) : (
                <div className="space-y-4">
                  {/* 用户名（不可编辑） */}
                  <div className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-sm text-gray-500">用户名</span>
                    <span className="text-sm font-medium text-gray-800">{user?.username || "-"}</span>
                  </div>

                  {/* 动态字段 */}
                  {profileFields.map((field) => {
                    const isImageType = field.type === "image" || field.type === "file";
                    const isSelectType = field.type === "select";
                    const isRadioType = field.type === "radio";
                    const isCheckboxType = field.type === "checkbox";
                    const isTextareaType = field.type === "textarea";
                    return (
                      <div key={field.key} className="flex items-center justify-between py-2 border-b border-gray-50">
                        <span className="text-sm text-gray-500">{field.label}</span>
                        {isEditing ? (
                          isImageType ? (
                            <div className="flex items-center gap-2">
                              {editData[field.key] || field.value ? (
                                <div className="relative group">
                                  <img
                                    src={editData[field.key] || field.value}
                                    alt={field.label}
                                    className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                  />
                                  <a
                                    href={editData[field.key] || field.value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg text-white text-xs"
                                  >
                                    查看
                                  </a>
                                </div>
                              ) : (
                                <div className="w-16 h-16 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
                                  <i className="ri-image-add-line text-gray-300" />
                                </div>
                              )}
                              <button
                                onClick={() => imageInputRefs.current[field.key]?.click()}
                                disabled={isUploading}
                                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:border-orange-400 hover:text-orange-500 transition-colors disabled:opacity-50"
                              >
                                {isUploading ? '上传中...' : '上传图片'}
                              </button>
                              <input
                                ref={(el) => { imageInputRefs.current[field.key] = el; }}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageFieldUpload(field.key, e)}
                              />
                            </div>
                          ) : isTextareaType ? (
                            <textarea
                              value={editData[field.key] || ""}
                              onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                              placeholder={`请输入${field.label}`}
                              className="text-sm font-medium text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 w-64 focus:outline-none focus:border-orange-400 resize-none"
                              rows={3}
                            />
                          ) : isSelectType ? (
                            <select
                              value={editData[field.key] || ""}
                              onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                              className="text-sm font-medium text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 w-64 focus:outline-none focus:border-orange-400"
                            >
                              <option value="">请选择{field.label}</option>
                              {field.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : isRadioType ? (
                            <div className="flex items-center gap-3 flex-wrap">
                              {field.options.map((opt) => (
                                <label key={opt} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={field.key}
                                    value={opt}
                                    checked={editData[field.key] === opt}
                                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                                    className="text-orange-500 focus:ring-orange-400"
                                  />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          ) : isCheckboxType ? (
                            <div className="flex items-center gap-3 flex-wrap">
                              {field.options.map((opt) => {
                                const currentValues = (editData[field.key] || "").split(",").filter(Boolean);
                                const isChecked = currentValues.includes(opt);
                                return (
                                  <label key={opt} className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      value={opt}
                                      checked={isChecked}
                                      onChange={(e) => {
                                        const newValues = e.target.checked
                                          ? [...currentValues, opt]
                                          : currentValues.filter((v) => v !== opt);
                                        setEditData({ ...editData, [field.key]: newValues.join(",") });
                                      }}
                                      className="text-orange-500 focus:ring-orange-400"
                                    />
                                    {opt}
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <input
                              type={field.type === "number" ? "number" : "text"}
                              value={editData[field.key] || ""}
                              onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                              placeholder={`请输入${field.label}`}
                              className="text-sm font-medium text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 w-64 focus:outline-none focus:border-orange-400"
                            />
                          )
                        ) : (
                          <span className="text-sm font-medium text-gray-800 text-right max-w-xs truncate">
                            {isImageType ? (
                              field.value ? (
                                <a
                                  href={field.value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-orange-500 hover:text-orange-600 underline"
                                >
                                  查看{field.label}
                                </a>
                              ) : (
                                <span className="text-gray-400">未上传</span>
                              )
                            ) : (
                              field.value || <span className="text-gray-400">未填写</span>
                            )}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* 无字段提示 */}
                  {!fieldsLoading && profileFields.length === 1 && (
                    <div className="py-4 text-center text-sm text-gray-400">
                      暂无注册信息
                    </div>
                  )}
                </div>
              )}
            </div>

          {/* 消息提示 */}
          {message && (
            <div className={`mt-4 p-3 ${message.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'} text-sm rounded-xl text-center`}>
              {message.text}
            </div>
          )}
        </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </DashboardLayout>
  );
}
